// Products.js
import React, { useState } from "react";
import "./Products.css";
import {
  FaCheckCircle,
  FaMicrochip,
  FaServer,
  FaCloud,
  FaRobot,
  FaLaptopCode,
  FaDatabase,
  FaRocket,
  FaHeadset,
} from "react-icons/fa";
import { FaBuilding, FaMobileAlt, FaGlobe } from "react-icons/fa";

// 🔹 Project-related product images (make sure these files exist)
import hrmsImg from "../../images/HRMS.png";
import amsImg from "../../images/AMSS.png";
import sgsImg from "../../images/SGS.png";
import petalsPathImg from "../../images/flowers-bowl.avif";
import ecommerceImg from "../../images/Ecommerce.png";
import Matchmaking from "../../images/jusstmingle-logo.png";
import jusstmingleAdmin from "../../images/jusstmingle-logo.png";
import iCareImage from "../../images/iCareImage.png";
// import safiya from "../../images/Safiyaa.png"
import rakshen from "../../images/Rakshak.png"
import { Link } from "react-router-dom";

const technologies = [
  {
    name: "AI & Machine Learning",
    icon: <FaRobot />,
    features: [
      "Predictive Analytics",
      "Chatbots & Automation",
      "Computer Vision",
    ],
    bgColor: "#f8f9fa",
  },
  {
    name: "Cloud Infrastructure",
    icon: <FaCloud />,
    features: [
      "AWS & Azure Integration",
      "Scalable Deployment",
      "Data Backup Solutions",
    ],
    bgColor: "#fffde7",
  },
  {
    name: "IoT & Embedded Systems",
    icon: <FaMicrochip />,
    features: ["Smart Devices", "Sensor Integration", "Edge Computing"],
    bgColor: "#f3e5f5",
  },
  {
    name: "Enterprise Applications",
    icon: <FaBuilding />,
    features: [
      "ERP/CRM to run ops",
      "One login (SSO)",
      "Reports & audit trails",
    ],
    bgColor: "#e8f5e9",
  },
  {
    name: "Web Solutions",
    icon: <FaGlobe />,
    features: ["Fast React/Next.js", "SEO-friendly", "Easy content updates"],
    bgColor: "#e3f2fd",
  },
  {
    name: "Mobile App Development",
    icon: <FaMobileAlt />,
    features: ["iOS & Android apps", "Push & payments", "Publish to stores"],
    bgColor: "#fff3e0",
  },
];

const serviceHighlights = [
  {
    icon: <FaLaptopCode />,
    title: "Custom Software Development",
    description:
      "Scalable, maintainable, and secure code for web and mobile platforms.",
  },
  {
    icon: <FaServer />,
    title: "Server Management",
    description:
      "24/7 monitoring, automation, and optimized hosting environments.",
  },
  {
    icon: <FaDatabase />,
    title: "Big Data & Analytics",
    description:
      "Transform raw data into powerful business insights and decisions.",
  },
  {
    icon: <FaRocket />,
    title: "Tech Consultancy",
    description:
      "Strategy, architecture, and agile processes to accelerate your success.",
  },
  {
    icon: <FaHeadset />,
    title: "24/7 Customer Support",
    description:
      "Always-on help via chat, email, and phone with SLA-based response.",
  },
];

// ====== PRODUCTS ======
const products = [
  {
    id: 1,
    name: "HRMS",
    title: "Human Resource Management System (HRMS)",
    image: hrmsImg,
    description:
      "A Human Resource Management System (HRMS) is a comprehensive digital platform designed to manage and automate HR processes within an organization. It covers employee information, payroll, attendance and leave, recruitment, performance management, and self-service portals for staff.",
    features: [
      "Payroll Management: Automates salary calculations based on attendance, overtime, and deductions.",
      "Attendance & Leave Management: Tracks employee attendance, working hours, and absences.",
      "Recruitment & Onboarding: Streamlines hiring with job posting, applicant tracking, and interview scheduling.",
      "Performance Management: Helps set goals, track achievements, and provide feedback.",
      "Improve Accuracy: Automatically records attendance to reduce manual mistakes.",
      "Fair & Timely Payments: Ensures salaries are based on accurate working hours & overtime.",
      "Compliance with Labor Laws: Supports adherence to legal rules for work hours and leave.",
      "Better Work–Life Balance: Clear schedules and overtime tracking for healthier planning.",
    ],
    outcomes: [
      "Improved payroll accuracy",
      "Fair & timely salary processing",
      "Better HR compliance & visibility",
    ],
    stack: ["Web Portal", "Cloud Ready", "Role-based Access"],
  },
  {
    id: 2,
    name: "AMS",
    title: "Attendance Management System (AMS)",
    image: amsImg,
    description:
      "An Attendance Management System (AMS) for workers is built to track and manage attendance in real time across sites such as factories, construction, or large organizations. It automates attendance capture, reduces manual effort, and minimizes errors.",
    features: [
      "Real-time Attendance Tracking via biometric, QR code, or RFID.",
      "Leave Management with approval workflows.",
      "Reporting & Analytics on punctuality, absenteeism, and overtime.",
      "Payroll Integration so attendance flows straight into salary calculations.",
    ],
    outcomes: [
      "Higher accuracy vs. manual registers",
      "Faster payroll processing",
      "Clear visibility into workforce presence",
    ],
    stack: ["Web Panel", "Biometric/QR Integration", "Exportable Reports"],
  },
  {
    id: 3,
    name: "SGS",
    title: "Man Power Solutions / Security Guard System (SGS)",
    image: sgsImg,
    description:
      "The Security Guard System (SGS) is a platform to manage and monitor security personnel and operations. It tracks patrols, attendance, incidents, and tasks to ensure accountability and better security coverage.",
    features: [
      "Guard Scheduling & Attendance with shift management.",
      "Incident Reporting with photos and videos from mobile devices.",
      "Workforce Management so supervisors can assign tasks digitally.",
      "Visitor & Access Control with logs and vehicle entries.",
      "Compliance tracking for guard certifications and regulatory needs.",
    ],
    outcomes: [
      "Improved accountability on patrols",
      "Better incident visibility for supervisors",
      "Stronger access control & audit trail",
    ],
    stack: ["Mobile + Web", "Location & Time Tracking", "Reporting Dashboard"],
  },
  {
    id: 4,
    name: "Petals Path",
    title: "Petals Path – Flowers, Buffets & Gift Hampers",
    image: petalsPathImg,
    description:
      "Petals Path is an eCommerce platform for online shopping of flowers, buffets, and decorative items. One place for high-quality floral arrangements, catering, and curated gift hampers.",
    features: [
      "Wide Floral Collections with customizable bouquets and seasonal sets.",
      "Buffet & Catering Services for weddings, parties, and corporate events.",
      "Custom Gift Hampers and curated combos for all occasions.",
      "Simple, modern shopping experience from browse to checkout.",
      "Advanced Search & Filters by flower, occasion, service, and price.",
      "Real-Time Order Tracking and delivery status.",
      "Multiple Payment Options including UPI, cards, and wallets.",
      "Subscription & Pre-Booking for events and recurring deliveries.",
    ],
    outcomes: [
      "All flowers & catering in one place",
      "Time-saving for event planning",
      "Highly customizable gifting & décor",
    ],
    stack: ["E-Commerce", "Payment Gateway", "Order Tracking"],
  },
  {
    id: 5,
    name: "E-Commerce",
    title: "Custom E-Commerce Solutions",
    image: ecommerceImg,
    description:
      "Our E-Commerce solutions provide a robust digital platform for selling products or services online. From catalog to checkout and order tracking, everything is performance-focused.",
    features: [
      "Product Catalog Management with rich media and variants.",
      "Secure Payment Gateway supporting all major methods.",
      "Order Tracking for customers and operators.",
      "Customer Support Tools including chat flows and tickets.",
      "Customer Accounts, wishlists, and purchase history.",
    ],
    outcomes: [
      "Reach customers beyond physical locations",
      "24/7 online storefront",
      "Lower operational overhead vs. offline-only",
    ],
    stack: ["Responsive Storefront", "Admin Panel", "Analytics & Reports"],
  },

  {
    id: 6,
    name: "Jusst Mingle App",
    title: "Jusst Mingle — AI-Assisted Dating & Matchmaking",
    image: Matchmaking,
    description:
      "A modern dating experience that helps people connect faster and safer with smart matching, privacy controls, and real-time chat.",
    features: [
       "Smart profile matching (interests, preferences, behavior)",
      "Real-time chat with media sharing",
      "AI chatbot for conversation starters & safety tips",
      "User verification (selfie/ID) & report/ban flow",
      "Location-based discovery with privacy controls",
      "In-app notifications & activity feed"
    ],
    outcomes: [
      "↑ 31% match-to-chat conversion", "↓ 42% fake/abuse reports", "Scales to realtime traffic",
    ],
    stack: ["React Native", "Node.js", "PostgreSQL", "AWS"],
  },

  {
    id: 7,
    name: "Jusst Mingle Admin",
    title: "Jusst Mingle — Admin Dashboard & Moderation Suite",
    description:
      "Centralized control for operations, trust & safety, and communications—built for visibility and fast actions.",
    image: jusstmingleAdmin, // placeholder; swap to your admin screenshot when ready
    features: [
      "Today’s active users & new registrations",
      "Gender split (male/female/other) & cohort views",
      "Suspended users & reported users queue",
      "Bulk notifications and targeted user messages",
      "Role-based access control & audit logs",
      "Config flags (feature toggles, limits, thresholds)"
    ],
    outcomes: ["Faster moderation SLAs", "Clear daily ops snapshot", "Scalable user comms"],
    stack: ["React (Web) / React Native", "PostgreSQL", "AWS"],
  },

  {
  id: 8,
  name: "iCare Foundation",
  title: "iCare Foundation — Unified NGO Operations System",
  description:
    "A complete NGO operations platform to manage members, volunteers, donations, workshops, media, partners, ID cards, and community help requests — all from one unified dashboard.",
  image: iCareImage, // replace with your actual screenshot import
  features: [
    "Membership onboarding with auto ID-card generation",
    "Volunteer enrolment, verification & active volunteer tracking",
    "Donation management with history, filters & analytics",
    "Workshop creation, enrollment tracking & certificate issuing",
    "Drive request workflow with agent-assisted submissions",
    "Partner onboarding with document verification",
    "Help-request submission, status tracking & admin assignment",
    "Blog & circular CMS with publishing tools",
    "Media management — photos, videos, press releases, categories",
    "Role-based admin access, activity logs & secure authentication"
  ],
  outcomes: [
    "Centralized NGO operations",
    "Reduced manual work with automation",
    "Transparent, trackable community workflows",
    "Enhanced volunteer & member engagement"
  ],
  stack: ["React.js (Frontend)", "Node.js (Backend)", "PostgreSQL (pgAdmin)", "REST APIs"]
},

{
  id: 9,
  name: "rakshen",
  title: "rakshen",
  description:
    "A combined hardware and software safety solution that tracks a child's real-time location using a smart wearable GPS-enabled watch paired with a secure mobile application.",
  image: rakshen, // replace with your actual screenshot import
  features: [
    "Custom-built smart wristwatch with GPS, SOS button & secure IoT connectivity",
    "Real-time child location tracking with geo-fencing alerts",
    "Parent mobile dashboard for monitoring movements & safety zones",
    "Emergency SOS alert triggers instant notifications",
    "Background tracking optimized for battery efficiency",
    "Secure communication between device, backend & mobile app",
    "Device registration & monitoring through admin console"
  ],
  outcomes: [
    "Enhanced child safety & parental peace of mind",
    "Instant emergency response capability",
    "Reliable real-time location tracking",
    "Efficient and secure IoT communication"
  ],
  stack: [
    "React Native (Mobile App)",
    "Spring Boot (Backend)",
    "IoT Hardware",
    "GPS Module",
    "REST APIs"
  ]
},

];

// 🔹 New: Mandatory-type section = rollout / implementation process
const implementationSteps = [
  {
    step: "1",
    title: "Discovery & Fit",
    text: "We understand your processes, users, and current tools to confirm the right product and modules.",
  },
  {
    step: "2",
    title: "Configuration & Integrations",
    text: "We configure features, roles, and connect to biometrics, payroll, or other systems you already use.",
  },
  {
    step: "3",
    title: "Pilot & Training",
    text: "We run a pilot with a smaller group, train users, and collect feedback before full rollout.",
  },
  {
    step: "4",
    title: "Go-Live & Ongoing Support",
    text: "We move your organization live with monitoring, updates, and SLAs for long-term success.",
  },
];

const Products = () => {
  const [selected, setSelected] = useState(products[0]);

  return (
    <div className="product-page">
      <h2 className="product-title">Innovative Technologies We Offer</h2>
      <p className="product-subtitle">
        Empowering your business through cutting-edge development &amp;
        cloud-first strategies.
      </p>

      {/* Technologies */}
      <div className="plans-container">
        {technologies.map((tech, index) => (
          <div
            className="plan-card"
            style={{ backgroundColor: tech.bgColor }}
            key={index}
          >
            <div className="plan-icon">{tech.icon}</div>
            <h3>{tech.name}</h3>
            <ul className="feature-list">
              {tech.features.map((feature, idx) => (
                <li key={idx}>
                  <FaCheckCircle className="check-icon" /> {feature}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Services / Highlights */}
      <section className="insta-style-section">
        <h2 className="interactive-title">Our Software Services</h2>
        <p className="interactive-subtitle">
          Everything you need to build, scale, and launch your digital product.
        </p>

        <div className="interactive-container">
          {serviceHighlights.map((feature, index) => (
            <div className="interactive-card" key={index}>
              <div className="interactive-icon">{feature.icon}</div>
              <h4>{feature.title}</h4>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Capabilities */}
      <section className="capabilities-section">
        <h2 className="capabilities-title">What Sets Us Apart</h2>
        <p className="capabilities-subtitle">
          Explore our key strengths in software development and enterprise IT.
        </p>

        <div className="capabilities-grid">
          <div className="capability-card">
            <h4>🌐 Scalable Architecture</h4>
            <p>
              Designed to grow as your business evolves — no rework required.
            </p>
          </div>
          <div className="capability-card">
            <h4>🔒 Security First</h4>
            <p>
              Encryption, compliance, and governance baked into every layer.
            </p>
          </div>
          <div className="capability-card">
            <h4>🚀 Rapid Development</h4>
            <p>Agile and DevOps practices that accelerate go-to-market.</p>
          </div>
          <div className="capability-card">
            <h4>🧠 Full-Stack Innovation</h4>
            <p>From cloud backend to front-end UX, handled by one team.</p>
          </div>
        </div>
      </section>

      {/* NEW: Implementation / Rollout Section */}
      <section className="implementation-section">
        <h2 className="implementation-title">How We Implement Your Product</h2>
        <p className="implementation-subtitle">
          A clear rollout path so your team adopts the product smoothly and sees
          value quickly.
        </p>

        <div className="implementation-grid">
          {implementationSteps.map((item) => (
            <div className="implementation-card" key={item.step}>
              <span className="implementation-step-badge">{item.step}</span>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Product Switcher */}
      <div className="product-section">
        <aside className="product-sidebar">
          <h2>Our Products</h2>
          {products.map((p) => (
            <button
              key={p.id}
              className={`product-button ${
                selected.id === p.id ? "active" : ""
              }`}
              onClick={() => setSelected(p)}
            >
              {p.name}
            </button>
          ))}
        </aside>

        <div className="product-details">
          <div className="product-image-top">
            {selected.image && (
              <img
                src={selected.image}
                alt={selected.name}
                className="product-image-preview"
                loading="lazy"
              />
            )}
            <h3 className="product-image-name">{selected.name}</h3>
          </div>

          <div className="product-info">
            <h4 className="product-info-title">{selected.title}</h4>
            <p className="product-info-desc">{selected.description}</p>

            {selected.outcomes?.length > 0 && (
              <>
                <h5 className="product-info-heading">Outcomes</h5>
                <div className="chip-row">
                  {selected.outcomes.map((o, i) => (
                    <span className="chip" key={i}>
                      {o}
                    </span>
                  ))}
                </div>
              </>
            )}

            <h5 className="product-info-heading">Key Features</h5>
            <ul className="product-features">
              {selected.features.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>

            {selected.stack?.length > 0 && (
              <>
                <h5 className="product-info-heading">Tech Stack</h5>
                <div className="chip-row">
                  {selected.stack.map((t, i) => (
                    <span className="chip alt" key={i}>
                      {t}
                    </span>
                  ))}
                </div>
              </>
            )}
            <Link to="/contact">
              <button className="demo-button">Request a Demo</button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
